import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Modal from 'react-bootstrap/Modal';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import './dashboard.css';
import './dashboardDesign.css';
import LeaseApiService from '../../services/apiService';

const apiService = new LeaseApiService();

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_COLORS = { leased: '#4A2C86', available: '#1E7A4F', blocked: '#B3261E', unknown: '#8B88A0' };
const FALLBACK_COLORS = ['#4A2C86', '#1F6682', '#B07A1F', '#C2477E', '#1E7A4F', '#7C5CC4', '#B3261E', '#64748B'];
const statusColor = (name, i) => STATUS_COLORS[String(name).toLowerCase()] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];

// Icon components (kept consistent with the existing dashboard visual language)
const RefreshIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

// Routes for card navigation
const ROUTES = {
  customer: '/Customer/Property/Master/Customer/Listing',
  property: '/Customer/Property/Master/Property/Listing',
  unit: '/Customer/Property/Master/Unit/Listing',
  unitBlock: '/Customer/Property/Master/UnitRelease/Listing',
};

// Defensive multi-casing readers - backend field casing has been inconsistent
// across these endpoints elsewhere in this codebase (see unitBlock/listing.js).
const getUnitProperty = (row) => row?.property_Name || row?.propertyName || row?.Property_Name || 'Unknown';
const getUnitStatus = (row) => row?.status || row?.unitStatus || row?.Status || 'Unknown';
const getBlockingProperty = (row) => row?.propertyName || row?.property_Name || row?.PropertyName || 'Unknown';
const getBlockingStatus = (row) => row?.status || row?.unitStatus || row?.Status || 'Unknown';

const buildLast12MonthsBuckets = (rows, dateField) => {
  const buckets = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    buckets.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      month: `${MONTH_LABELS[d.getMonth()]} ${d.getFullYear()}`,
      count: 0,
    });
  }
  rows.forEach((row) => {
    const raw = row?.[dateField];
    if (!raw) return;
    const d = new Date(raw);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const bucket = buckets.find((b) => b.key === key);
    if (bucket) bucket.count += 1;
  });
  return buckets;
};

const groupByAccessor = (rows, accessor, limit) => {
  const counts = {};
  rows.forEach((row) => {
    const key = accessor(row) || 'Unknown';
    counts[key] = (counts[key] || 0) + 1;
  });
  const arr = Object.entries(counts).map(([name, count]) => ({ name, count }));
  arr.sort((a, b) => b.count - a.count);
  return limit ? arr.slice(0, limit) : arr;
};

const EXPIRY_DAY_OPTIONS = [7, 15, 30, 60, 90];

// Document types come from a free-text dropdown, so categories are matched by name.
const EXPIRY_CATEGORIES = [
  { id: 'passport', label: 'Passport expiring', theme: 'c-purple', accent: '#4A2C86', match: /passport/i },
  { id: 'id', label: 'ID expiring', theme: 'c-green', accent: '#1E7A4F', match: /\bid\b|emirates|national|identity|iqama|residen/i },
  { id: 'trade', label: 'Trade licence / CR expiring', theme: 'c-gold', accent: '#B07A1F', match: /trade|licen[cs]e|\bcr\b|commercial/i },
];

const getExpiryCategory = (docType) => {
  const text = String(docType || '');
  const hit = EXPIRY_CATEGORIES.find((c) => c.match.test(text));
  return hit ? hit.id : null;
};

const isZeroDate =(val) => !val || val === '0' || String(val).startsWith('1899') || String(val).startsWith('0001');

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

// Runs `worker` over `items` with at most `limit` requests in flight.
const runWithConcurrency = async (items, limit, worker) => {
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      await worker(item);
    }
  });
  await Promise.all(runners);
};

// Horizontal bars; widths animate in after mount.
const HBars = ({ rows, colorFn }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(false);
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
    return () => cancelAnimationFrame(id);
  }, [rows]);
  if (!rows.length) return <div className="cd-empty">No data to show.</div>;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="cd-hbar">
      {rows.map((r, i) => (
        <div className="row" key={r.name}>
          <div className="lab" title={r.name}>{r.name}</div>
          <div className="track"><div className="fill" style={{ width: ready ? `${(r.count / max) * 100}%` : 0, background: colorFn(r, i) }} /></div>
          <div className="val num">{r.count.toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
};

const MonthTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return <div className="cd-tip"><b>{p.month}</b><br />Customers <span className="num">{p.count}</span></div>;
};

const pct = (n, t) => (t ? Math.round((n / t) * 100) : 0);

const CustomerPropertyDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customerCount, setCustomerCount] = useState(0);
  const [propertyCount, setPropertyCount] = useState(0);
  const [unitRows, setUnitRows] = useState([]);
  const [unitTotal, setUnitTotal] = useState(0);
  const [blockingRows, setBlockingRows] = useState([]);
  const [blockingTotal, setBlockingTotal] = useState(0);
  const [customerMonthlyData, setCustomerMonthlyData] = useState([]);
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [expiryDays, setExpiryDays] = useState(30);
  const [expiryModal, setExpiryModal] = useState(null);
  const [expiryLoading, setExpiryLoading] = useState(false);
  const [refreshSpin, setRefreshSpin] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    await Promise.all([fetchCustomers(), fetchProperties(), fetchUnits(), fetchUnitBlockings()]);
    setLoading(false);
  };

  const fetchCustomers = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 2000);
      params.append('FullName', '');
      const res = await apiService.getApiParamater('GET_CUSTOMER', `?${params.toString()}`);
      const rows = res?.data?.result || [];
      setCustomerCount(res?.data?.paginationData?.totalRecords ?? rows.length);
      setCustomerMonthlyData(buildLast12MonthsBuckets(rows, 'createdOn'));
      // Documents are only exposed per customer, so this runs in the background
      // without holding up the rest of the dashboard.
      fetchExpiringDocuments(rows);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchExpiringDocuments = async (customers) => {
    setExpiryLoading(true);
    const found = [];
    try {
      await runWithConcurrency(customers.filter((c) => c?.customerNo), 8, async (customer) => {
        try {
          const params = new URLSearchParams();
          params.append('Action', 'GetDocumentsByCustomerNo');
          params.append('TenantNo', customer.customerNo);
          const res = await apiService.getApiParamater('CUSTOMER_COMMON', `?${params.toString()}`);
          toArray(res?.data?.result?.data).forEach((doc) => {
            const raw = doc?.doc_ExpiryDate ?? doc?.Doc_ExpiryDate;
            if (isZeroDate(raw)) return;
            const d = new Date(raw);
            if (isNaN(d)) return;
            const documentType = doc?.document_Type ?? doc?.Document_Type ?? 'Unknown';
            const category = getExpiryCategory(documentType);
            if (!category) return;
            const docNo = doc?.document_No ?? doc?.Document_No;
            found.push({
              customerNo: customer.customerNo,
              customerName: customer.customerName || customer.customerNo,
              documentType,
              documentNo: docNo && docNo !== '0' ? String(docNo) : '-',
              expiryDate: d,
              category,
            });
          });
        } catch (error) {
          console.error('Error fetching documents for customer', customer.customerNo, error);
        }
      });
    } finally {
      found.sort((a, b) => a.expiryDate - b.expiryDate);
      setExpiringDocs(found);
      setExpiryLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 1);
      params.append('Community', '');
      params.append('PropertyName', '');
      const res = await apiService.getApiParamater('GET_PROPERTY_LIST', `?${params.toString()}`);
      setPropertyCount(res?.data?.paginationData?.totalRecords ?? (res?.data?.result || []).length);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchUnits = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 2000);
      const res = await apiService.getApiParamater('GET_UNIT_LIST', `?${params.toString()}`);
      const rows = res?.data?.result || [];
      setUnitRows(rows);
      setUnitTotal(res?.data?.paginationData?.totalRecords ?? rows.length);
    } catch (error) {
      console.error('Error fetching units:', error);
    }
  };

  const fetchUnitBlockings = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', 1);
      params.append('PageSize', 2000);
      params.append('PropertyNo', '');
      params.append('UnitNo', '');
      params.append('Status', '');
      const res = await apiService.getApiParamater('GET_ALL_UNIT_BLOCKINGS_RELEASE', `?${params.toString()}`);
      const rows = res?.data?.result || [];
      setBlockingRows(rows);
      setBlockingTotal(res?.data?.paginationData?.totalRecords ?? rows.length);
    } catch (error) {
      console.error('Error fetching unit blockings:', error);
    }
  };

  const isStatus = (row, matches) => matches.includes(getUnitStatus(row).toLowerCase());
  const availableCount = unitRows.filter((r) => isStatus(r, ['available'])).length;

  const windowEnd = new Date();
  windowEnd.setHours(23, 59, 59, 999);
  windowEnd.setDate(windowEnd.getDate() + expiryDays);
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const docsInWindow = expiringDocs.filter((d) => d.expiryDate >= todayStart && d.expiryDate <= windowEnd);
  const expiryCards = EXPIRY_CATEGORIES.map((cat) => ({
    ...cat,
    docs: docsInWindow.filter((d) => d.category === cat.id),
  }));
  const activeExpiryCard = expiryCards.find((c) => c.id === expiryModal);

  const expiryColumns = [
    { headerName: 'Customer No', field: 'customerNo', width: 140 },
    { headerName: 'Customer Name', field: 'customerName', flex: 1, minWidth: 180 },
    { headerName: 'Document Type', field: 'documentType', width: 180 },
    { headerName: 'Document No', field: 'documentNo', width: 160 },
    {
      headerName: 'Expiry Date',
      field: 'expiryDate',
      width: 140,
      valueFormatter: (p) => (p.value ? p.value.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
    },
    {
      headerName: 'Days Left',
      width: 110,
      valueGetter: (p) => Math.ceil((p.data.expiryDate - todayStart) / 86400000),
    },
  ];

  const unitsByProperty = groupByAccessor(unitRows, getUnitProperty, 10);
  const unitsByStatus = groupByAccessor(unitRows, getUnitStatus);
  const blockingByProperty = groupByAccessor(blockingRows, getBlockingProperty, 10);
  const blockingByStatus = groupByAccessor(blockingRows, getBlockingStatus);

  const handleCardClick = (route) => {
    if (route) navigate(route);
  };

  const spin = refreshSpin;
  const setSpin = setRefreshSpin;
  const refresh = async () => {
    setSpin(true);
    await fetchDashboardData();
    setTimeout(() => setSpin(false), 600);
  };

  if (loading) {
    return <div className="cd-page"><div className="cd-loading">Loading dashboard data...</div></div>;
  }

  const statusCount = (name) => unitsByStatus.find((x) => x.name.toLowerCase() === name)?.count || 0;
  const leasedCount = statusCount('leased');
  const blockedStatus = statusCount('blocked');
  const customerTotalInPeriod = customerMonthlyData.reduce((a, p) => a + p.count, 0);
  const tiles = [
    { c: 'c-purple', name: 'Total customers', n: customerCount, dot: '#4A2C86', note: 'Registered customers', route: ROUTES.customer },
    { c: 'c-blue', name: 'Total properties', n: propertyCount, dot: '#1F6682', note: 'Buildings and villas', route: ROUTES.property },
    { c: 'c-green', name: 'Available units', n: availableCount, dot: '#1E7A4F', note: `${pct(availableCount, unitTotal)}% of units`, bar: pct(availableCount, unitTotal), route: ROUTES.unit },
    { c: 'c-red', name: 'Blocked units', n: blockingTotal, dot: '#B3261E', note: `${pct(blockingTotal, unitTotal)}% of units`, bar: Math.max(pct(blockingTotal, unitTotal), blockingTotal ? 2 : 0), route: ROUTES.unitBlock },
  ];

  return (
    <div className="dashboard-container cd-page">
      <div className="cd-titlebar">
        <div>
          <h1>Customer Dashboard</h1>
          <p className="cd-lede">Portfolio, occupancy and document status at a glance.</p>
        </div>
        <button type="button" className={`cd-btn${spin ? ' spin' : ''}`} onClick={refresh}><RefreshIcon />Refresh</button>
      </div>

      {/* KPI strip */}
      <section className="cd-card cd-summary" aria-label="Portfolio summary">
        <div className="cd-tile cd-tile-main">
          <div className="cd-main-top"><span>Total units</span><span className="num">{propertyCount} properties</span></div>
          <div className="cd-main-val"><span className="num">{unitTotal.toLocaleString()}</span><em>units under management</em></div>
          <div className="cd-main-legend">
            <span><i className="cd-dot" style={{ background: '#C9B8F0' }} />Leased <span className="num">{leasedCount}</span></span>
            <span><i className="cd-dot" style={{ background: '#8FD6B0' }} />Available <span className="num">{availableCount}</span></span>
            <span><i className="cd-dot" style={{ background: '#F29A93' }} />Blocked <span className="num">{blockedStatus || blockingTotal}</span></span>
          </div>
        </div>
        <div className="cd-tiles">
          {tiles.map((t) => (
            <div className={`cd-tile clickable ${t.c}`} key={t.name} onClick={() => handleCardClick(t.route)} role="link" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && handleCardClick(t.route)}>
              <div className="cd-tlabel"><span className="cd-sq" style={{ background: t.dot }} />{t.name}</div>
              <div className="num cd-big">{t.n.toLocaleString()}</div>
              <div className="cd-sub">{t.note}</div>
              {t.bar !== undefined && <div className="cd-meter" aria-hidden="true"><i style={{ width: `${t.bar}%`, background: t.dot }} /></div>}
            </div>
          ))}
        </div>
      </section>

      {/* Documents expiring */}
      <section className="cd-card c-gold" aria-label="Documents expiring">
        <div className="cd-chead">
          <div>
            <h2>Documents expiring</h2>
            <div className="cd-sub">{expiryLoading ? 'Checking customer documents...' : 'Customer documents due for renewal'}</div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--muted)' }}>
            Expiring within
            <select className="cd-select" value={expiryDays} onChange={(e) => setExpiryDays(Number(e.target.value))}>
              {EXPIRY_DAY_OPTIONS.map((n) => <option key={n} value={n}>{n} days</option>)}
            </select>
          </label>
        </div>
        <div className="cd-docs">
          {expiryCards.map((card) => (
            <div className={`cd-doc ${card.theme}`} key={card.id}>
              <div className="cd-doc-top">
                <div className="cd-tlabel"><span className="cd-sq" style={{ background: card.accent }} />{card.label}</div>
                <span className={`cd-badge ${card.docs.length ? 'warn' : 'ok'}`}>{card.docs.length ? 'Action needed' : 'All clear'}</span>
              </div>
              <div className="cd-doc-val">
                <button
                  type="button"
                  className={`num cd-big${card.docs.length ? ' link' : ''}`}
                  title={card.docs.length ? 'View list' : ''}
                  onClick={() => card.docs.length && setExpiryModal(card.id)}
                >
                  {card.docs.length.toLocaleString()}
                </button>
                <span className="cd-sub">documents</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Modal show={!!activeExpiryCard} onHide={() => setExpiryModal(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {activeExpiryCard?.label} - next {expiryDays} days
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ag-theme-quartz" style={{ height: 420, width: '100%' }}>
            <AgGridReact
              rowData={activeExpiryCard?.docs || []}
              columnDefs={expiryColumns}
              defaultColDef={{ sortable: true, resizable: true, filter: true }}
              pagination
              paginationPageSize={10}
            />
          </div>
        </Modal.Body>
      </Modal>

      {/* Customers by month */}
      <section className="cd-card c-purple" aria-label="Customers by month">
        <div className="cd-chead">
          <div><h2>Customers by month</h2><div className="cd-sub">New customers, last 12 months</div></div>
          <div className="cd-sub num">{customerTotalInPeriod.toLocaleString()} customers in period</div>
        </div>
        <div className="cd-cbody">
          {customerMonthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={customerMonthlyData} margin={{ top: 16, right: 18, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="cdArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#4A2C86" stopOpacity={0.25} />
                    <stop offset="1" stopColor="#4A2C86" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#fff" strokeOpacity={0.9} vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8B88A0' }} tickFormatter={(m) => m.slice(0, 3)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#8B88A0' }} axisLine={false} tickLine={false} allowDecimals={false} width={36} />
                <Tooltip content={<MonthTip />} cursor={{ stroke: '#C9A961', strokeDasharray: '3 3' }} />
                <Area type="monotone" dataKey="count" stroke="#4A2C86" strokeWidth={2.5} fill="url(#cdArea)" dot={false} activeDot={{ r: 5, fill: '#C9A961', stroke: '#fff', strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="cd-empty">No data to show.</div>
          )}
        </div>
      </section>

      <div className="cd-grid2">
        <section className="cd-card c-blue" aria-label="Units by property">
          <div className="cd-chead"><div><h2>Units by property</h2><div className="cd-sub">Top 10 properties</div></div></div>
          <div className="cd-cbody"><HBars rows={unitsByProperty} colorFn={(r, i) => (i === 0 ? '#4A2C86' : '#7C5CC4')} /></div>
        </section>
        <section className="cd-card c-green" aria-label="Units by status">
          <div className="cd-chead"><div><h2>Units by status</h2><div className="cd-sub">Across all properties</div></div></div>
          <div className="cd-cbody">
            <HBars rows={unitsByStatus} colorFn={(r, i) => statusColor(r.name, i)} />
            <div className="cd-legend">
              {unitsByStatus.map((x, i) => (
                <span key={x.name}><i className="cd-sq" style={{ background: statusColor(x.name, i) }} />{x.name} · {pct(x.count, unitRows.length)}%</span>
              ))}
            </div>
          </div>
        </section>
      </div>

      <div className="cd-grid2">
        <section className="cd-card c-red" aria-label="Blocked units by property">
          <div className="cd-chead"><div><h2>Blocked units by property</h2><div className="cd-sub">Top 10 properties</div></div></div>
          <div className="cd-cbody"><HBars rows={blockingByProperty} colorFn={() => '#B3261E'} /></div>
        </section>
        <section className="cd-card c-gold" aria-label="Unit blocking by status">
          <div className="cd-chead"><div><h2>Unit blocking by status</h2><div className="cd-sub">Current blocking requests</div></div></div>
          <div className="cd-cbody"><HBars rows={blockingByStatus} colorFn={(r, i) => statusColor(r.name, i)} /></div>
        </section>
      </div>
    </div>
  );
};

export default CustomerPropertyDashboard;
